import { Fragment } from "react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

function getCircleClass(isActive, isCompleted) {
  return isActive || isCompleted
    ? "border-primary-600 bg-primary-600 text-white"
    : "border-gray-300 bg-white text-gray-400";
}

function CircleIcon({
  stepNumber,
  isCompleted,
  isFinalSuccess,
  isFinalFailed,
  size,
}) {
  const iconSize = size === "lg" ? 20 : 16;
  if (isFinalSuccess) return <FaCheckCircle style={{ fontSize: iconSize }} />;
  if (isFinalFailed) return <FaTimesCircle style={{ fontSize: iconSize }} />;
  if (isCompleted) return <FaCheckCircle style={{ fontSize: iconSize }} />;
  return stepNumber;
}

function HorizontalStepper({ steps, currentStep, finalStatus }) {
  return (
    <div className="flex items-start w-full">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isCompleted = currentStep > stepNumber;
        const isActive = currentStep === stepNumber;
        const isLast = index === steps.length - 1;
        const isFinalStep = index === steps.length - 1;
        const isFinalSuccess = isFinalStep && finalStatus === "SUCCESS";
        const isFinalFailed = isFinalStep && finalStatus === "FAILED";

        return (
          <Fragment key={step.label}>
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 text-sm font-bold flex-shrink-0 ${getCircleClass(isActive, isCompleted)}`}
              >
                <CircleIcon
                  stepNumber={stepNumber}
                  isCompleted={isCompleted}
                  isFinalSuccess={isFinalSuccess}
                  isFinalFailed={isFinalFailed}
                  size="sm"
                />
              </div>
              <span
                className={`text-[10px] mt-1 text-center font-semibold w-16 leading-tight ${
                  isCompleted || isActive ? "text-primary-600" : "text-gray-400"
                }`}
              >
                {step.label}
              </span>
              {step.description && (
                <span className="hidden sm:block text-[9px] text-center text-gray-400 w-14 leading-tight mt-0.5">
                  {step.description}
                </span>
              )}
            </div>

            {!isLast && (
              <div
                className={`flex-1 h-0.5 mt-4 mx-1 ${
                  currentStep > stepNumber ? "bg-primary-600" : "bg-gray-200"
                }`}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function VerticalStepper({ steps, currentStep, finalStatus }) {
  return (
    <div className="flex flex-col items-start w-48 py-4 h-full">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isCompleted = currentStep > stepNumber;
        const isActive = currentStep === stepNumber;
        const isLast = index === steps.length - 1;
        const isFinalStep = index === steps.length - 1;
        const isFinalSuccess = isFinalStep && finalStatus === "SUCCESS";
        const isFinalFailed = isFinalStep && finalStatus === "FAILED";

        return (
          <Fragment key={step.label}>
            <div className="flex items-start justify-between w-full">
              <div className="flex flex-col text-left pr-2">
                <h3
                  className={`text-sm font-semibold ${
                    isCompleted || isActive
                      ? "text-primary-600"
                      : "text-gray-600"
                  }`}
                >
                  {step.label}
                </h3>
                {step.description && (
                  <p className="text-xs text-gray-500 mt-1">
                    {step.description}
                  </p>
                )}
              </div>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 text-lg font-bold flex-shrink-0 ${getCircleClass(isActive, isCompleted)}`}
              >
                <CircleIcon
                  stepNumber={stepNumber}
                  isCompleted={isCompleted}
                  isFinalSuccess={isFinalSuccess}
                  isFinalFailed={isFinalFailed}
                  size="lg"
                />
              </div>
            </div>

            {!isLast && (
              <div
                className={`flex-1 w-0.5 min-h-[2rem] ml-auto mr-5 my-1 ${
                  isCompleted ? "bg-primary-600" : "bg-gray-200"
                }`}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

export function StepIndicator({
  steps,
  currentStep = 1,
  layout = "horizontal",
  finalStatus,
}) {
  if (layout === "horizontal") {
    return (
      <HorizontalStepper
        steps={steps}
        currentStep={currentStep}
        finalStatus={finalStatus}
      />
    );
  }

  if (layout === "vertical") {
    return (
      <VerticalStepper
        steps={steps}
        currentStep={currentStep}
        finalStatus={finalStatus}
      />
    );
  }

  return (
    <>
      <div className="sm:hidden w-full mb-6 px-1">
        <HorizontalStepper
          steps={steps}
          currentStep={currentStep}
          finalStatus={finalStatus}
        />
      </div>
      <div className="hidden sm:flex mr-8 self-stretch">
        <VerticalStepper
          steps={steps}
          currentStep={currentStep}
          finalStatus={finalStatus}
        />
      </div>
    </>
  );
}

export default StepIndicator;
